import { useCallback, useEffect, useRef, useState } from "react";
import { LuCloudUpload, LuFile, LuFileSpreadsheet, LuFileText, LuImage, LuX } from "react-icons/lu";
import { toast } from "react-toastify";
import { fetchDocumentUploadUrl } from "../../utils/apiUtils.js";
import "./FileUpload.scss";

const ACCEPT = [
    "application/pdf",
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/tiff",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "text/csv",
    "text/plain",
].join(",");

const SOURCES = [
    { value: "ringmaster", label: "Ringmaster" },
    { value: "stoploss", label: "Stoploss" },
];

const REPORT_TYPES_BY_SOURCE = {
    ringmaster: [
        { value: "outstanding_reimbursement", label: "Outstanding Reimbursement" },
    ],
    stoploss: [
        { value: "agg_accommodation", label: "Agg Accommodation" },
        { value: "agg_balance", label: "Agg Balance" },
    ],
};

const STATUS_LABEL = {
    ready: "Ready",
    uploading: "Uploading…",
    done: "Uploaded",
    error: "Failed",
};

const formatBytes = (n) => {
    if (n == null || Number.isNaN(n)) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const fileIconFor = (file) => {
    const type = file.type || "";
    const name = file.name || "";
    if (type.startsWith("image/")) return LuImage;
    if (type === "application/pdf" || name.toLowerCase().endsWith(".pdf")) return LuFileText;
    if (
        type.includes("spreadsheet")
        || type.includes("excel")
        || /\.(xlsx|xls|csv)$/i.test(name)
    ) {
        return LuFileSpreadsheet;
    }
    return LuFile;
};

const FileUpload = () => {
    const inputRef = useRef(null);
    const selectedRef = useRef(null);
    const [selected, setSelected] = useState(null);
    const [source, setSource] = useState("");
    const [reportType, setReportType] = useState("");
    const [isDragging, setIsDragging] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    selectedRef.current = selected;

    const reportTypeOptions = source ? REPORT_TYPES_BY_SOURCE[source] || [] : [];

    useEffect(() => () => {
        if (selectedRef.current?.previewUrl) {
            URL.revokeObjectURL(selectedRef.current.previewUrl);
        }
    }, []);

    const setSelectedFile = useCallback((file) => {
        setSelected((prev) => {
            if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
            if (!file) return null;
            return {
                id: crypto.randomUUID(),
                file,
                previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
                status: "ready",
                error: null,
                s3Key: null,
            };
        });
    }, []);

    const takeSingleFile = useCallback((fileList) => {
        const incoming = Array.from(fileList || []);
        if (!incoming.length) return;

        if (incoming.length > 1) {
            toast.info("Only one file can be uploaded at a time. Using the first file.", {
                autoClose: 4000,
                hideProgressBar: true,
            });
        }

        setSelectedFile(incoming[0]);
    }, [setSelectedFile]);

    const clearFile = () => {
        setSelectedFile(null);
        if (inputRef.current) inputRef.current.value = "";
    };

    const handleSourceChange = (nextSource) => {
        setSource(nextSource);
        setReportType("");
    };

    const handleDrop = (event) => {
        event.preventDefault();
        setIsDragging(false);
        if (isUploading) return;
        takeSingleFile(event.dataTransfer.files);
    };

    const handleUpload = async () => {
        if (!selected || !source || !reportType || isUploading) return;
        if (selected.status !== "ready" && selected.status !== "error") return;

        setIsUploading(true);
        setSelected((prev) => (prev ? { ...prev, status: "uploading", error: null } : prev));

        try {
            const contentType = selected.file.type || "application/octet-stream";
            const { url, s3Key } = await fetchDocumentUploadUrl({
                source,
                reportType,
                fileName: selected.file.name,
                contentType,
                timestamp: new Date().toISOString(),
            });

            const res = await fetch(url, {
                method: "PUT",
                body: selected.file,
                headers: { "Content-Type": contentType },
            });

            if (!res.ok) {
                throw new Error(`S3 upload failed (${res.status})`);
            }

            setSelected((prev) => (prev ? { ...prev, status: "done", s3Key, error: null } : prev));
            toast.success("File uploaded successfully.", {
                autoClose: 4000,
                hideProgressBar: true,
            });
        } catch (err) {
            const message = err?.message || "Upload failed";
            console.error(`Upload failed for ${selected.file.name}:`, err);
            setSelected((prev) => (prev ? { ...prev, status: "error", error: message } : prev));
            toast.error(`Upload failed: ${message}`, {
                autoClose: 8000,
                hideProgressBar: true,
            });
        } finally {
            setIsUploading(false);
        }
    };

    const canUpload = Boolean(
        source
        && reportType
        && !isUploading
        && selected
        && (selected.status === "ready" || selected.status === "error"),
    );

    const Icon = selected ? fileIconFor(selected.file) : null;

    return (
        <div className="file-upload container scrollable-form">
            <p className="title">Analytics Upload</p>
            <p className="sub-title">
                Choose a source and report type, then upload one file to the data management landing bucket.
            </p>

            <div className="file-upload__filters">
                <div className="form-input file-upload__filter">
                    <label htmlFor="file-upload-source">Source</label>
                    <select
                        id="file-upload-source"
                        value={source}
                        onChange={(event) => handleSourceChange(event.target.value)}
                        disabled={isUploading}
                    >
                        <option value="" disabled>
                            Select a source
                        </option>
                        {SOURCES.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="form-input file-upload__filter">
                    <label htmlFor="file-upload-report-type">Report Type</label>
                    <select
                        id="file-upload-report-type"
                        value={reportType}
                        onChange={(event) => setReportType(event.target.value)}
                        disabled={isUploading || !source}
                    >
                        <option value="" disabled>
                            {source ? "Select a report type" : "Select a source first"}
                        </option>
                        {reportTypeOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT}
                hidden
                disabled={isUploading}
                onChange={(event) => {
                    takeSingleFile(event.target.files);
                    event.target.value = "";
                }}
            />

            <div
                className={`file-upload__dropzone${isDragging ? " file-upload__dropzone--active" : ""}${isUploading ? " file-upload__dropzone--disabled" : ""}`}
                onDragEnter={(event) => {
                    event.preventDefault();
                    if (!isUploading) setIsDragging(true);
                }}
                onDragOver={(event) => {
                    event.preventDefault();
                    if (!isUploading) setIsDragging(true);
                }}
                onDragLeave={(event) => {
                    if (!event.currentTarget.contains(event.relatedTarget)) {
                        setIsDragging(false);
                    }
                }}
                onDrop={handleDrop}
                onClick={() => {
                    if (!isUploading) inputRef.current?.click();
                }}
                onKeyDown={(event) => {
                    if (isUploading) return;
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        inputRef.current?.click();
                    }
                }}
                role="button"
                tabIndex={isUploading ? -1 : 0}
                aria-disabled={isUploading}
                aria-label="Choose a file to upload"
            >
                <LuCloudUpload size="2rem" />
                <p className="file-upload__dropzone-title">
                    {isDragging ? "Drop file here" : "Drag and drop a file here"}
                </p>
                <p className="file-upload__dropzone-hint">
                    or click to browse. One file at a time. PDF, images, Excel, Word, and CSV are supported.
                </p>
            </div>

            {selected && (
                <div className="file-upload__list">
                    <div className="file-upload__list-header">
                        <p className="file-upload__list-title">Selected file</p>
                        <button
                            type="button"
                            className="file-upload__clear"
                            onClick={clearFile}
                            disabled={isUploading}
                        >
                            Clear
                        </button>
                    </div>

                    <ul className="file-upload__items">
                        <li className="file-upload__item">
                            <div className="file-upload__item-icon">
                                {selected.previewUrl
                                    ? <img src={selected.previewUrl} alt="" />
                                    : <Icon size="1.1rem" />}
                            </div>
                            <div className="file-upload__item-body">
                                <p className="file-upload__item-name" title={selected.file.name}>
                                    {selected.file.name}
                                </p>
                                <p className="file-upload__item-meta">
                                    {formatBytes(selected.file.size)}
                                    {selected.error
                                        ? ` · ${selected.error}`
                                        : selected.s3Key
                                            ? ` · ${selected.s3Key}`
                                            : selected.file.type
                                                ? ` · ${selected.file.type}`
                                                : ""}
                                </p>
                            </div>
                            <span className={`file-upload__status file-upload__status--${selected.status}`}>
                                {STATUS_LABEL[selected.status] || selected.status}
                            </span>
                            <button
                                type="button"
                                className="file-upload__remove"
                                onClick={clearFile}
                                disabled={isUploading || selected.status === "uploading"}
                                aria-label={`Remove ${selected.file.name}`}
                            >
                                <LuX size="1rem" />
                            </button>
                        </li>
                    </ul>
                </div>
            )}

            <div className="file-upload__actions">
                <button
                    type="button"
                    className="btn edit-btn"
                    disabled={!canUpload}
                    onClick={handleUpload}
                >
                    {isUploading ? "Uploading…" : "Upload"}
                </button>
            </div>
        </div>
    );
};

export default FileUpload;
